/**
 * Display formatting for US phone numbers on the directory forms.
 *
 * A ten-digit number typed as 4156095729 reads back as (415) 609-5729. The
 * stored value keeps the punctuation — it is what a visitor sees on the
 * listing — and ListingCard strips it again for the tel: link, so nothing
 * downstream depends on the shape.
 *
 * Anything that is not plainly a US number is returned exactly as typed:
 * a foreign number, an extension, an ordinary "call the front desk" note.
 * Guessing at those and getting it wrong is worse than leaving them alone.
 */

/** (415) 609-5729, and the partial forms on the way there. */
function group(digits, prefix = "") {
  if (!digits) return "";
  // Below four digits there is nothing to punctuate yet, and adding "(" after
  // a single keystroke makes the field feel like it is fighting the typist.
  if (digits.length <= 3) return `${prefix}${digits}`;
  if (digits.length <= 6) {
    return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `${prefix}(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/** Digits and country code, or null when this is not a formattable US number. */
function usDigits(raw) {
  // Letters mean an extension or a note — not something to reformat.
  if (/[a-z]/i.test(raw)) return null;

  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return { digits: digits.slice(1), prefix: "+1 " };
  }
  // A leading + that is not +1 is some other country's number.
  if (raw.trim().startsWith("+")) return null;
  if (digits.length > 10) return null;
  return { digits, prefix: "" };
}

/**
 * Display formatting. Only a complete number is punctuated: nine digits shaped
 * as "(287) 328-910" would look like a US number that cannot be dialled, so a
 * partial value is shown exactly as it was stored.
 */
export function formatUsPhone(value) {
  const raw = String(value ?? "");
  const parsed = usDigits(raw);
  if (!parsed || parsed.digits.length !== 10) return raw;
  return group(parsed.digits, parsed.prefix);
}

/** Formatting mid-keystroke, where a partial number is expected. */
function formatWhileTyping(value) {
  const raw = String(value ?? "");
  const parsed = usDigits(raw);
  if (!parsed) return raw;
  return group(parsed.digits, parsed.prefix);
}

/**
 * The change handler for a phone input.
 *
 * Formats forward only. Reformatting on delete leaves the caret fighting the
 * punctuation — backspace over the ")" in "(415) " re-adds it and the field
 * appears stuck — so a deletion passes through untouched and the next typed
 * character puts the shape back.
 */
export function nextPhoneValue(previous, next) {
  const shorter = String(next).length < String(previous ?? "").length;
  return shorter ? next : formatWhileTyping(next);
}
